import { toast as sonner } from "sonner";
import type { ReactNode, ReactElement } from "react";

type ToastPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

export interface ToastOptions {
  position?: ToastPosition;
  autoClose?: number | false;
  hideProgressBar?: boolean;
  closeOnClick?: boolean;
  pauseOnHover?: boolean;
  draggable?: boolean;
  pauseOnFocusLoss?: boolean;
  theme?: "light" | "dark" | "system";
  className?: string;
  bodyClassName?: string;
  icon?: ReactNode;
  toastId?: string | number;
  closeButton?: boolean;
  onClose?: () => void;
  onClick?: () => void;
  data?: Record<string, unknown>;
  isLoading?: boolean;
}

export interface ToastContentProps {
  closeToast: () => void;
  toastProps: { toastId: string | number };
}

export interface ToastPromiseOptions<T = unknown> {
  pending?: ReactNode | ((data: T) => ReactNode);
  success?: ReactNode | ((data: T) => ReactNode);
  error?: ReactNode | ((err: unknown) => ReactNode);
}

function mapOptions(opts?: ToastOptions) {
  if (!opts) return undefined;
  const mapped: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(opts)) {
    if (value === undefined) continue;
    if (key === "autoClose") {
      mapped.duration = value === false ? Number.POSITIVE_INFINITY : value;
    } else if (key === "toastId") {
      mapped.id = value;
    } else if (key === "onClose") {
      mapped.onDismiss = value;
    } else if (key === "closeButton") {
      mapped.closeButton = value;
    } else if (key === "icon" || key === "className") {
      mapped[key] = value;
    } else if (key === "bodyClassName") {
      mapped.className = mapped.className
        ? `${mapped.className} ${value}`
        : value;
    }
  }
  return mapped;
}

function custom(
  content: (ctx: ToastContentProps) => ReactNode,
  opts?: ToastOptions,
) {
  return sonner.custom((id) => {
    const closeToast = () => sonner.dismiss(id);
    return content({ closeToast, toastProps: { toastId: id } }) as ReactElement;
  }, mapOptions(opts));
}

function toastFunction(
  message: ReactNode | ((ctx: ToastContentProps) => ReactNode),
  opts?: ToastOptions,
) {
  if (typeof message === "function") {
    return custom(message, opts);
  }
  return sonner(message, mapOptions(opts));
}

function createMethod(
  name: "success" | "error" | "info" | "warning" | "loading",
) {
  return (message: ReactNode, opts?: ToastOptions) =>
    sonner[name](message, mapOptions(opts));
}

const toast = Object.assign(toastFunction, {
  success: createMethod("success"),
  error: createMethod("error"),
  info: createMethod("info"),
  warning: createMethod("warning"),
  warn: createMethod("warning"),
  loading: createMethod("loading"),
  dismiss: sonner.dismiss,
  promise: <T>(promise: Promise<T>, opts: ToastPromiseOptions<T>) => {
    return sonner.promise(promise, {
      loading: opts.pending as ReactNode,
      success: opts.success as unknown as ReactNode,
      error: opts.error as unknown as ReactNode,
    });
  },
  custom,
  update: (
    id: string | number,
    options: {
      render?: ReactNode;
      type?: "success" | "error" | "info" | "warning" | "default";
    } & ToastOptions,
  ) => {
    const { render, type, isLoading, ...rest } = options;
    const message = render ?? "";
    const mapped = mapOptions(rest) ?? {};
    mapped.id = id;

    if (isLoading) {
      return sonner.loading(message, mapped);
    }

    if (type && type !== "default") {
      return sonner[type](message, mapped);
    }
    return sonner(message, mapped);
  },
});

export { toast };
export { Toaster } from "@/components/ui/sonner";
